import { defineStore } from "pinia";

import type { ALMIssue } from "@/backend/natsstubs/almsync/common/models";
import { fetchAlmIssues } from "@/backend/read/almIssues";
import { errorMessage } from "@/error/errorUnwrapper";
import { captureException } from "@/error/sentry";
import type { Color } from "@/model/baseTypes";
import { almTypeInfo } from "@/model/baseTypes";
import type { BoardCard } from "@/model/card";
import { isArtLevelWorkitem } from "@/model/stickyType";
import { useGlobalI18n } from "@/translations/i18n";

import { useBoardStore } from "./board";
import { useLinkStore } from "./link";
import { useSessionStore } from "./session";
import { useStickyTypeStore } from "./stickyType";
import { useTeamStore } from "./team";

const { t } = useGlobalI18n();

export interface IssueSelectorJQLQuerySection {
  name: string;
  value: string;
  isDefault: boolean;
}

interface IssueSelectorJQLQuery {
  sections: IssueSelectorJQLQuerySection[];
  separator: string;
}

export interface LinkedToItem {
  id: string;
  almId: string;
  text: string;
  type: { color: Color };
  teamId?: string;
  teamName: string;
}

/** Convert a board card to a LinkedToItem for use in the WIS "linked to" filter */
export function boardCardToLinkedToItem(card: BoardCard): LinkedToItem {
  const { almId, text, type, teamId } = card.data;
  const team = teamId ? useTeamStore().findTeam({ id: teamId }) : undefined;
  return {
    id: card.data.id,
    almId: almId!,
    text,
    type: { color: type.color },
    teamId: teamId ?? undefined,
    teamName: team?.name ?? "",
  };
}

export const useIssueSelectorStore = defineStore("issueSelector", {
  state: () => ({
    active: false,
    issues: [] as ALMIssue[],
    totalCount: 0,
    limit: 0,
    isLoading: false,
    error: null as string | null,
    text: null as string | null,
    selectedTypeIds: [] as string[],
    selectedAssigneeIds: [] as string[], // pip user IDs (special ID "no" = unassigned, backend translates to ALM IDs)
    query: null as IssueSelectorJQLQuery | null,
    lastReloadedAt: null as Date | null,
    selectedLinkedToItems: [] as LinkedToItem[],
  }),
  getters: {
    /**
     * Returns backlog workitem item types which can be mirrored to the team board
     */
    backlogItemTypes: () =>
      useStickyTypeStore().stickyTypes.filter(
        (type) =>
          type.functionality === "workitem" &&
          type.origin === "backlog" &&
          type.usable.includes("team"),
      ),
    hasIssues: (state) => state.issues.length > 0,
    hasActiveFilter: (state) =>
      !!state.text ||
      !!state.selectedTypeIds.length ||
      !!state.selectedAssigneeIds.length ||
      !!state.selectedLinkedToItems.length,
    linkedToAlmIds: (state) => state.selectedLinkedToItems.map((item) => item.almId),
    showAlmItemsSection: () => {
      const almType = useSessionStore().almType;
      return almTypeInfo[almType].hasIssueSelector;
    },
    /**
     * The ALM Issue Selector is disabled when the team doesn't have a useable project mapping
     * - no project
     * - multiple projects (not yet supported)
     *
     * Returns a message explaining to the user why
     */
    disabledMessage: () => {
      switch (useBoardStore().board?.almSources.length) {
        case 0:
        case undefined:
          return t("issueSelector.disabled.noSource");
        case 1:
          // The case we support
          return undefined;
        default:
          // TODO: Remove when multi-project support is added
          return t("issueSelector.disabled.multipleSources");
      }
    },
    /**
     * Returns true if the issue selector is disabled (eg. if there's no alm mapping for the current board)
     */
    disabled(): boolean {
      return !!this.disabledMessage;
    },
  },
  actions: {
    async fetchIssues() {
      // If the issue selector is disabled, don't fetch anything
      if (this.disabled) {
        this.reset();
        return;
      }

      const sessionId = useSessionStore().current.id;
      const teamId = useTeamStore().current.id;

      if (!sessionId || !teamId) {
        this.error = "Missing session or team";
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.query = null;

      try {
        // Convert sticky type IDs to ALM issue type IDs (e.g., Jira issue type IDs)
        const almTypeIds = useStickyTypeStore().getAlmTypeIds(this.selectedTypeIds);

        const response = await fetchAlmIssues(teamId, sessionId, {
          text: this.text || undefined,
          almTypeIds,
          assigneeIds: this.selectedAssigneeIds,
          linkedTo: this.linkedToAlmIds,
        });
        this.issues = response.results;
        this.totalCount = response.total;
        this.limit = response.limit;
        this.query = response.query || null;
        this.lastReloadedAt = new Date();
      } catch (err) {
        this.error = errorMessage(err, "Failed to load issues");
        captureException(err);
      } finally {
        this.isLoading = false;
      }
    },
    setText(text: string) {
      this.text = text.trim() || null;
    },
    setSelectedTypeIds(ids: string[]) {
      this.selectedTypeIds = ids;
    },
    setSelectedAssigneeIds(ids: string[]) {
      this.selectedAssigneeIds = ids;
    },
    setSelectedLinkedToItems(items: LinkedToItem[]) {
      this.selectedLinkedToItems = items;
    },
    toggle(active?: boolean) {
      if (active === undefined ? !this.active : active) {
        this.active = true;
        if (this.showAlmItemsSection) {
          const pinnedCards = useLinkStore().markingCardsLinkedCards;
          const pinnedArtCard = pinnedCards?.find(
            (c) => isArtLevelWorkitem(c.data) && c.data.almId,
          );
          if (pinnedArtCard) {
            this.selectedLinkedToItems = [boardCardToLinkedToItem(pinnedArtCard)];
          }
          void this.fetchIssues();
        }
      } else {
        this.active = false;
        this.reset();
      }
    },
    reset() {
      // Reset all state (used on close and session change)
      this.issues = [];
      this.totalCount = 0;
      this.limit = 0;
      this.error = null;
      this.query = null;
      this.lastReloadedAt = null;
      this.resetFilters();
      useLinkStore().removeAllMarks();
    },
    resetFilters() {
      this.text = null;
      this.selectedTypeIds = [];
      this.selectedAssigneeIds = [];
      this.selectedLinkedToItems = [];
    },
    resetFiltersAndFetch() {
      this.resetFilters();
      void this.fetchIssues();
    },
    removeIssue(issueId: string) {
      const index = this.issues.findIndex((issue) => issue.issueId === issueId);
      if (index !== -1) {
        this.issues.splice(index, 1);
        if (this.totalCount > 0) {
          this.totalCount--;
        }
      }
    },
  },
});
